// Pure helpers for the auth screens (S1 accounts). Kept free of React so they are easy to unit test.
import { isApiError } from '@/lib/api'
import { analyticsAllowed } from '@/lib/consentStore'
import { t } from '@/lib/i18n'

/** "maya.chen@example.com" becomes "m***@example.com" (the verify screen shows where the code went). */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return ''
  const at = email.indexOf('@')
  if (at <= 0) return email
  const name = email.slice(0, at)
  const domain = email.slice(at + 1)
  const head = name.slice(0, 1)
  return `${head}***@${domain}`
}

/** `guessable`: the API refuses it as too easy to guess (score 0). */
export type PasswordStrength = { score: 0 | 1 | 2 | 3 | 4; label: string; ok: boolean; guessable?: boolean }

const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const

// SEC2-AUTH-09 as the API checks it (backend services/passwords.py is_common): a password whose letters alone, 4 or
// more, spell the site's name or one of the person's own words is refused, whatever digits and symbols it adds.
const SERVICE_WORDS = ['mgai', 'mgaischool', 'mgschool', 'mgaipro', 'mgcafe', 'mgaiyoutube', 'skool']
const letters = (value: string | null | undefined) => (value ?? '').toLowerCase().replace(/[^a-z]+/g, '')

/** The person's own words a guesser tries first (backend auth.password_context): email local part and names. */
export function passwordContext(email?: string | null, firstName?: string | null, lastName?: string | null): string[] {
  return [(email ?? '').trim().split('@')[0], firstName ?? '', lastName ?? '', `${firstName ?? ''}${lastName ?? ''}`]
}

/**
 * 0 = shorter than 8 characters, or the person's name, email or the site's name with digits and symbols added
 * (both refused by the API; FVJ-6: the meter said Strong for "JoanRivera2026!"), then one point each for length 12
 * or more, mixed case, a digit and a symbol, capped at 4. Common patterns lose a point.
 */
export function passwordStrength(password: string, context: string[] = []): PasswordStrength {
  if (password.length < 8) return { score: 0, label: STRENGTH_LABELS[0], ok: false }
  const core = letters(password)
  if (core.length >= 4 && [...SERVICE_WORDS, ...context.map(letters)].some((w) => w.length >= 4 && w === core)) {
    return { score: 0, label: 'Too easy to guess', ok: false, guessable: true }
  }
  let points = 1
  if (password.length >= 12) points += 1
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) points += 1
  if (/\d/.test(password)) points += 1
  if (/[^A-Za-z0-9]/.test(password)) points += 1
  if (/^(.)\1+$/.test(password) || /^(password|12345678|qwerty|letmein)/i.test(password)) points -= 1
  const score = Math.max(1, Math.min(4, points)) as 1 | 2 | 3 | 4
  return { score, label: STRENGTH_LABELS[score], ok: true }
}

/** Keeps digits only, at most `length` of them (OTP paste and typing). */
export function sanitizeCode(value: string, length = 6): string {
  return value.replace(/\D/g, '').slice(0, length)
}

/** Seconds left on a cooldown that started at `startedAt` (ms) and lasts `seconds`. */
export function cooldownLeft(startedAt: number | null, seconds: number, now = Date.now()): number {
  if (!startedAt) return 0
  return Math.max(0, Math.ceil((startedAt + seconds * 1000 - now) / 1000))
}

/** Friendly text for the ?error= codes the Google callback sends back to /login. */
/** Translated copy (literal t() calls) for the ?error= code the Google callback redirects with. */
export function googleErrorCopy(code: string | null | undefined): string | null {
  switch (code) {
    case null:
    case undefined:
    case '':
      return null
    case 'google_cancelled':
      return t('Google sign in was cancelled.')
    case 'email_in_use':
      return t('An account with that email already exists. Log in with your password, then link Google.')
    case 'email_not_allowed':
      return t('That email address cannot be used here.')
    case 'account_unavailable':
      return t('That account is not available. Contact support if you think this is a mistake.')
    case 'signups_paused': // G-02 kill switch: new accounts are paused, existing ones still sign in
      return t('New signups are paused for a little while. Please try again later.')
    default:
      return t('Google sign in did not work this time. Please try again.')
  }
}

export type FirstTouch = {
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
  utmTerm?: string | null
  referrer: string | null
  referringDomain?: string | null
  landing: string
  ts: string
}

export const FIRST_TOUCH_KEY = 'mgs-first-touch'

type LandingLocation = Pick<Location, 'search' | 'pathname' | 'origin'>

/** Remembers where a visitor first came from (UTM tags, referrer, landing path) for signup attribution and the
 *  analytics first touch. The caller saves it only once analytics are allowed (docs/analytics.md); `loc` is the
 *  address the visitor landed on (lib/analytics landingLocation), not wherever they are now. */
export function captureFirstTouch(storage: Storage | null = safeStorage(), loc: LandingLocation | null = safeLocation()): void {
  if (!storage || !loc) return
  try {
    if (storage.getItem(FIRST_TOUCH_KEY)) return
    const params = new URLSearchParams(loc.search)
    const ref = typeof document !== 'undefined' ? document.referrer : ''
    const external = ref && !ref.startsWith(loc.origin) ? ref.split(/[?#]/, 1)[0] : null
    let domain: string | null = null
    try {
      domain = external ? new URL(external).hostname : null
    } catch {
      domain = null
    }
    const touch: FirstTouch = {
      utmSource: params.get('utm_source'),
      utmMedium: params.get('utm_medium'),
      utmCampaign: params.get('utm_campaign'),
      utmContent: params.get('utm_content'),
      utmTerm: params.get('utm_term'),
      referrer: external,
      referringDomain: domain,
      landing: loc.pathname,
      ts: new Date().toISOString(),
    }
    storage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch))
  } catch {
    /* private mode: attribution is optional */
  }
}

/** The first touch to send with a signup (email or Google), only while analytics are allowed. */
export function attributionFirstTouch(): FirstTouch | null {
  return analyticsAllowed() ? readFirstTouch() : null
}

export function readFirstTouch(storage: Storage | null = safeStorage()): FirstTouch | null {
  try {
    const raw = storage?.getItem(FIRST_TOUCH_KEY)
    return raw ? (JSON.parse(raw) as FirstTouch) : null
  } catch {
    return null
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

function safeLocation(): Location | null {
  return typeof window !== 'undefined' ? window.location : null
}

/**
 * Splits a heading around the brand name so the brand can render as one unbreakable piece (Q-FAM-10:
 * a balanced title wrapped as "Create your MG / AI School account" at 390). Translations keep the
 * brand verbatim, so this works in every language. Odd indexes are the brand:
 * "Log in to MG AI School" gives ["Log in to ", "MG AI School", ""].
 */
export function splitBrand(text: string): string[] {
  return text.split(/(MG AI School)/)
}

/** G-20: the API wants the Turnstile check on the next login try (after a few failed logins, only when Turnstile is on). */
export function asksForCaptcha(error: unknown): boolean {
  if (!isApiError(error)) return false
  return error.fields.captcha === true || error.code === 'captcha_required' || error.code === 'captcha_failed'
}

/** Full page navigation (Google sign in leaves the app); an object so tests can replace `go`. */
export const browserNav = {
  go(url: string): void {
    window.location.href = url
  },
}
